import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Stack,
  ButtonGroup,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";
import { Table as CustomTable } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import { getFlipkartStatisticsStatusList } from "app/redux/slice/flipkartSlice";
import { usePagination } from "app/hooks/usePagination";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FLIPKART_STATISTICS } from "app/routes/constant";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const FlipkartStatisticsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status } = useParams();
  const location = useLocation();

  const flipkartData = useSelector((state) => state?.flipkart);
  const products = flipkartData?.statusList?.list ?? [];

  const initialDateRange = location.state
    ? [
        location.state?.fromDate ? new Date(location.state.fromDate) : null,
        location.state?.toDate ? new Date(location.state.toDate) : null,
      ]
    : [null, null];

  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [tempDateRange, setTempDateRange] = useState(initialDateRange);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: `flipkart-statistics-${status}` });

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, dateRange, order, orderBy]);

  const fetchList = () => {
    const params = {
      page,
      perPage: rowsPerPage,
      search: searchTerm.trim(),
    };
    if (dateRange[0] !== null && dateRange[1] !== null) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    dispatch(getFlipkartStatisticsStatusList({ id: status, data: params }));
  };

  const applyFilters = () => {
    setSearchTerm(tempSearchTerm);
    setDateRange(tempDateRange);
    handleChangePage(0, 0);
  };

  const clearAll = () => {
    setTempSearchTerm("");
    setSearchTerm("");
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    handleChangePage(0, 0);
  };

  const formatLabel = (s) => {
    const map = {
      DELIVERED: "Delivered",
      CANCELLED: "Cancelled",
      RETURNED: "Returned",
      RETURN_REQUESTED: "Return Requested",
      IN_TRANSIT: "In Transit",
      RETURN_CANCELLED: "Return Cancelled",
    };
    return map[s] || s;
  };

  const columns = [
    { id: "skuName", label: "SKU Name", sort: false },
    { id: "orderCount", label: "Order Count", sort: false },
  ];

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(FLIPKART_STATISTICS)}
          variant="outlined"
          size="small"
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          {formatLabel(status)} - Orders
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search orders..."
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.75}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempDateRange}
                  onChange={setTempDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3}>
              <ButtonGroup size="small" variant="outlined">
                {QUICK_RANGES.map((r) => (
                  <Button
                    key={r.key}
                    onClick={() => setTempDateRange(r.getRange())}
                  >
                    {r.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  fullWidth
                  size="small"
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearAll}
                  startIcon={<RefreshIcon />}
                  fullWidth
                  size="small"
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {flipkartData?.statusList?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={products}
              page={page}
              rowsPerPage={rowsPerPage}
              count={flipkartData?.statusList?.total || products.length}
              Order={order}
              OrderBy={orderBy}
              sortData={(o, ob) => {
                setOrder(o);
                setOrderBy(ob);
              }}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={flipkartData?.statusList?.loading}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FlipkartStatisticsList;
